import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../../context/FirebaseContext';
import { getAdminFeedback, deleteFeedback } from '../../api/feedback';
import { message } from 'antd';
import './FeedbackTab.css';

const FeedbackTab = () => {
    const { auth } = useFirebase();
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    const fetchFeedback = useCallback(async (pageNum) => {
        try {
            setLoading(true);
            const token = await auth.currentUser.getIdToken();
            const response = await getAdminFeedback(token, pageNum);

            if (response.error) {
                setError(response.error);
                return;
            }

            if (response.content) {
                if (pageNum === 0) {
                    setFeedback(response.content);
                } else {
                    setFeedback(prev => [...prev, ...response.content]);
                }
                setHasMore(!response.last);
            }
        } catch (err) {
            console.error('Error fetching feedback:', err);
            setError('Failed to load feedback');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchFeedback(0);
    }, [fetchFeedback]);

    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchFeedback(nextPage);
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation(); // Prevent expanding/collapsing when clicking delete
        
        if (!window.confirm('Are you sure you want to delete this feedback?')) {
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const result = await deleteFeedback(token, id);
            
            if (result.error) {
                message.error('Failed to delete feedback: ' + result.error);
                return;
            }

            message.success('Feedback deleted successfully');
            // Remove from local state
            setFeedback(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            console.error('Error deleting feedback:', err);
            message.error('Failed to delete feedback');
        }
    };

    const getUserDisplay = (item) => {
        const userName = item.userName || item.user_name;
        const userEmail = item.userEmail || item.user_email;
        
        if (userName) {
            return userName;
        }
        if (userEmail) {
            return userEmail;
        }
        // Fallback to userId if no name/email available
        return item.userId || item.user_id || 'Unknown User';
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return 'Unknown date';
        }
        return new Date(dateString).toLocaleString();
    };

    const getRatingStars = (rating) => {
        const safeRating = Math.max(0, Math.min(5, rating || 0));
        return '★'.repeat(safeRating) + '☆'.repeat(5 - safeRating);
    };

    const formatMetadata = (metadataValue) => {
        if (!metadataValue) {
            return null;
        }
        if (typeof metadataValue === 'string') {
            try {
                return JSON.stringify(JSON.parse(metadataValue), null, 2);
            } catch (error) {
                return metadataValue;
            }
        }
        return JSON.stringify(metadataValue, null, 2);
    };

    const getTypeClass = (type) => {
        switch (type) {
            case 'BUG': return 'feedback-type-bug';
            case 'FEATURE': return 'feedback-type-feature';
            default: return 'feedback-type-general';
        }
    };

    if (loading && page === 0) {
        return <div className="feedback-loading">Loading feedback...</div>;
    }

    if (error) {
        return <div className="feedback-error">{error}</div>;
    }

    return (
        <div className="feedback-tab">
            <div className="feedback-list">
                {feedback.map((item) => {
                    const createdAt = item.createdAt || item.created_at;
                    const content = item.content || '';
                    const metadata = formatMetadata(item.metadata);

                    return (
                        <div key={item.id} className="feedback-item">
                            <div className="feedback-header" onClick={() => toggleExpand(item.id)}>
                                <div className="feedback-primary-info">
                                    <span className={`feedback-type ${getTypeClass(item.type)}`}>{item.type}</span>
                                    <span className="feedback-rating">{getRatingStars(item.rating)}</span>
                                    <span className="feedback-date">{formatDate(createdAt)}</span>
                                </div>
                                <div className="feedback-header-right">
                                    <div className="feedback-user">User: {getUserDisplay(item)}</div>
                                    <button 
                                        className="feedback-delete-btn"
                                        onClick={(e) => handleDelete(item.id, e)}
                                        title="Delete feedback"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            <div className="feedback-content-preview">
                                {content.length > 100 ? `${content.substring(0, 100)}...` : content}
                            </div>

                            {expandedId === item.id && (
                                <div className="feedback-details">
                                    <div className="feedback-full-content">
                                        <h4>Full Comment:</h4>
                                        <p>{content}</p>
                                    </div>
                                    {metadata && (
                                        <div className="feedback-metadata">
                                            <h4>Metadata:</h4>
                                            <pre>{metadata}</pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {hasMore && (
                <button className="load-more-btn" onClick={loadMore} disabled={loading}>
                    {loading ? 'Loading...' : 'Load More'}
                </button>
            )}
        </div>
    );
};

export default FeedbackTab;
